import * as microsoftTeams from '@microsoft/teams-js';
import * as standaloneApp from '../internal/standalone-app';
import {
  AuthenticateParameters,
  AuthTokenRequest,
  CareerHubIFrameHostName,
  CareerHubSDKContext,
  DialogSubmitHandler,
  HostType,
  ICareerHubAppconfig,
  ICareerHubSdk,
  UserProfile,
  UserRequest,
} from './careerHub.types';
import { IDialogService } from './interfaces/IDialogService';
import { UrlDialogInfo } from '@microsoft/teams-js';

/**
 * Namespace to interact with the underlying SDK methods
 */
export namespace careerHubSdk {
  let internalHubSdk: ICareerHubSdk = microsoftTeams;
  let appConfigObj: ICareerHubAppconfig;

  /**
   * Returns teams context
   * @returns token: string
   */
  export const getContext = (): Promise<CareerHubSDKContext> => {
    return internalHubSdk.app.getContext();
  };

  export function executeDeepLink(link: string): void {
    internalHubSdk.app.openLink(link);
  }

  export function logout(): void {
    if (internalHubSdk.authentication.logout) internalHubSdk.authentication.logout();
  }

  /**
   * Returns user profile
   * @returns A user profile object
   */
  export const getUserProfile = (): Promise<UserProfile> => {
    return new Promise<UserProfile>((resolve, reject) => {
      const userRequest: UserRequest = {
        successCallback: (profile) => {
          resolve(profile);
        },
        failureCallback: (reason) => {
          reject(reason);
        },
      };
      internalHubSdk.authentication.getUser(userRequest);
    });
  };

  /**
   * Returns authorization toekn
   * @returns token: string
   */
  export const getAuthToken = (resource?: string): Promise<string> => {
    return new Promise<string>((resolve, reject) => {
      const tokenRequest: AuthTokenRequest = {
        resources: [resource || ''],
        successCallback: (token: string) => {
          resolve(token);
        },
        failureCallback: (reason: string) => {
          reject(reason);
        },
      };
      internalHubSdk.authentication.getAuthToken(tokenRequest);
    });
  };

  /**
   * returns App host type (Teams/Standalone)
   * @returns hostType: HostType - App host type
   */
  export const getHostType = (): HostType => {
    const microsoftTeamsLib = microsoftTeams || (window as any)?.microsoftTeams;

    if (!microsoftTeamsLib) {
      return HostType.Standalone; // the Microsoft Teams library is for some reason not loaded
    }

    // Standalone = No parent window and dialog module host name is @{CareerHubIFrameHostName}
    if (
      (window.parent === window.self && !(window as any).nativeInterface) ||
      window.name === CareerHubIFrameHostName
    ) {
      return HostType.Standalone;
    }
    return HostType.M365App;
  };

  /**
   * Authenticate via AAD popup
   */
  export function authenticate(url?: string) {
    return new Promise<string>((resolve, reject) => {
      const authParams: AuthenticateParameters = {
        url: url || '', // For standalone app
        successCallback: (result: string) => {
          resolve(result);
        },
        failureCallback: (reason: string) => {
          reject(reason);
        },
      };
      internalHubSdk.authentication.authenticate(authParams);
    });
  }

  /**
   * Returns career hub app config
   * @returns appConfigObj: ICareerHubAppconfig
   */
  export function getCareerHubAppConfig(): ICareerHubAppconfig {
    return appConfigObj;
  }

  /**
   * React hook: Initialize career hub sdk
   * @param options optional options
   * @returns A tuple with properties and methods
   * properties:
   *  - hostType: HostType - App host type (Teams, Standalone etc)
   *  - fullscreen: boolean = true if in full screen mode
   *  - theme: Fluent UI Theme
   *  - themeString: string - representation of the theme (default, dark or contrast)
   *  - context - the CareerHub SDK context
   * methods:
   *  - setTheme - manually set the theme
   */
  export function initialize(
    options?: {
      appConfig: ICareerHubAppconfig;
      initialTheme?: string;
      setThemeHandler?: (theme?: string) => void;
      useStandaloneSDKInTeams?: boolean;
      dialogService: IDialogService;
    },
    callBack?: (hostType: HostType, context: CareerHubSDKContext) => void
  ): void {
    const hostType = getHostType();
    console.log('Identified host name is ', hostType);
    // refresh context on successful sign-in
    const refreshContext = () => {
      internalHubSdk.app
        .getContext()
        .then((context) => callBack && callBack(hostType, context))
        .catch((reason) => console.log(reason));
    };

    if (options && options.appConfig) {
      appConfigObj = options?.appConfig;
      standaloneApp.registerAppConfig(options?.appConfig, options?.dialogService, refreshContext);
    }

    if (options?.useStandaloneSDKInTeams == true || hostType == HostType.Standalone) {
      internalHubSdk = standaloneApp;
      microsoftTeams.app.initialize().catch((_reason) => {}); // Hack: initialize Teams SDK to safeguard
    } else {
      internalHubSdk = microsoftTeams;
    }

    internalHubSdk.app
      .initialize()
      .then(() => {
        refreshContext();
        if (options?.setThemeHandler) {
          internalHubSdk.app.registerOnThemeChangeHandler(options?.setThemeHandler);
        }
      })
      .catch((reason) => console.log(reason));
  }

  export const startTask = (dialogInfo: UrlDialogInfo, submitHandler?: DialogSubmitHandler) => {
    const window = internalHubSdk.dialog.url.open(dialogInfo, submitHandler);
    return window;
  };

  export const submitTask = (result?: string | object, appIds?: string | string[]) => {
    internalHubSdk.dialog.url.submit(result, appIds);
  };
}
